'use client'

import Loader from '@/components/common/loader'
import SearchElement from '@/components/search/search-element'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { removeEmpty } from '@/functions/remove-empty'
import { trackEvent } from '@/lib/mixpanel'
import { SearchIcon } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

function getInitialFilter(searchParams = {}) {
  return {
    keyword: searchParams.keyword || '',
    platforms: searchParams.platforms
      ? searchParams.platforms.split(',')
      : ['Any'],
    game: searchParams.game || 'Any',
    days: searchParams.days ? searchParams.days.split(',') : ['Any'],
    carClasses: searchParams.carClasses
      ? searchParams.carClasses.split(',')
      : ['Any'],
  }
}

function isPopulated(value) {
  if (Array.isArray(value)) return value.length > 0
  return Boolean(value)
}

export default function HomepageSearchHeader({
  searchParams = {},
  games = [],
  platforms = [],
  languages = [],
  customSearch = null,
  hidePlatforms = false,
  hideGame = false,
  hideCarClass = true,
  hideDays = false,
  isSearching = false,
  textPlaceholder = 'Search communities, tracks etc...',
  linkPrefix = 'sim-racing-events',
}) {
  const router = useRouter()
  const [searching, setSearching] = useState(isSearching)
  const [filter, setFilter] = useState(() => getInitialFilter(searchParams))

  useEffect(() => {
    setSearching(false)
    setFilter(getInitialFilter(searchParams))
  }, [searchParams])

  const carClassOptions = useMemo(() => {
    if (!filter.game || filter.game === 'Any') return []
    const selectedGame = games.find((game) => game.name === filter.game)
    return (
      selectedGame?.carClasses?.map((carClass) => ({
        id: carClass.name,
        label: carClass.name,
        name: carClass.name,
        nicename: carClass.name,
      })) || []
    )
  }, [filter.game, games])

  const searchOptions = useMemo(
    () => [
      {
        id: 'platform',
        label: 'Platform',
        required: true,
        options: [{ name: 'Any', nicename: 'Any' }].concat(platforms),
      },
      {
        id: 'game',
        label: 'Game',
        grouped: true,
        groups: [
          { prop: 'id', compareVal: 'any', label: false },
          { prop: 'featured', compareVal: true, label: 'Popular' },
          { prop: 'featured', compareVal: false, label: 'Other Titles' },
        ],
        options: [
          {
            name: 'Any',
            featured: true,
            nicename: 'Any',
            carClasses: [],
          },
        ].concat(games),
      },
      {
        id: 'carClass',
        label: 'Car Class',
        options: [
          { id: 'any', label: 'Any', name: 'Any', nicename: 'Any' },
        ].concat(carClassOptions),
      },
      {
        id: 'days',
        label: 'Days',
        required: true,
        options: [
          { name: 'Any', nicename: 'Any', longname: 'Any' },
          { name: 'mon', nicename: 'Monday', longname: 'Monday' },
          { name: 'tue', nicename: 'Tuesday', longname: 'Tuesday' },
          { name: 'wed', nicename: 'Wednesday', longname: 'Wednesday' },
          { name: 'thu', nicename: 'Thursday', longname: 'Thursday' },
          { name: 'fri', nicename: 'Friday', longname: 'Friday' },
          { name: 'sat', nicename: 'Saturday', longname: 'Saturday' },
          { name: 'sun', nicename: 'Sunday', longname: 'Sunday' },
        ],
      },
      {
        id: 'language',
        label: 'Language',
        required: true,
        options: languages,
      },
    ],
    [carClassOptions, games, languages, platforms],
  )

  const canRunSearch = useMemo(() => {
    const requiredFields = []
    if (!hidePlatforms) requiredFields.push('platforms')
    if (!hideGame) requiredFields.push('game')
    if (!hideDays) requiredFields.push('days')
    return requiredFields.every((field) => isPopulated(filter[field]))
  }, [filter, hideDays, hideGame, hidePlatforms])

  function setGame(key, newValue) {
    if (filter[key] === newValue) {
      setFilter((prev) => ({ ...prev, [key]: 'Any', carClasses: ['Any'] }))
      return
    }

    const nextGame = newValue
    let nextCarClasses = filter.carClasses
    if (nextGame === 'Any') {
      nextCarClasses = ['Any']
    } else {
      const gameCarClasses =
        games.find((game) => game.name === nextGame)?.carClasses || []
      const selectedAreValid = (filter.carClasses || []).every(
        (carClassName) =>
          carClassName === 'Any' ||
          gameCarClasses.some((carClass) => carClass.name === carClassName),
      )
      if (!selectedAreValid) {
        nextCarClasses = ['Any']
      }
    }

    setFilter((prev) => ({
      ...prev,
      [key]: nextGame,
      carClasses: nextCarClasses,
    }))
  }

  function updateFilter(key, newValue) {
    setFilter((prev) => {
      const next = { ...prev }

      if (
        (key === 'platforms' || key === 'days' || key === 'carClasses') &&
        newValue === 'Any'
      ) {
        next[key] = ['Any']
        return next
      }

      const existingValues = Array.isArray(next[key]) ? next[key] : []
      if (existingValues.some((value) => value === newValue)) {
        next[key] = existingValues.filter((value) => value !== newValue)
      } else {
        next[key] = [...existingValues, newValue]
      }

      if (next[key].length === 0) {
        next[key] = ['Any']
      } else {
        next[key] = next[key].filter((value) => value !== 'Any')
      }

      return next
    })
  }

  function search() {
    if (!canRunSearch) return

    setSearching(true)
    const queryParams = removeEmpty(filter, {})
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(
        ([, value]) =>
          !(value === 'Any' || (Array.isArray(value) && value.includes('Any'))),
      ),
    )

    filteredParams.ts = new Date().getTime()
    delete filteredParams.name
    delete filteredParams.page

    if (searchParams.orderBy) {
      filteredParams.orderBy = searchParams.orderBy
    }

    const queryString = new URLSearchParams(filteredParams).toString()
    if (typeof customSearch === 'function') {
      customSearch(filteredParams)
      return
    }

    if (!queryString) {
      setSearching(false)
      return
    }

    if (linkPrefix === 'sim-racing-liveries/search') {
      trackEvent('Livery Search', filteredParams)
    } else {
      trackEvent('Event Search', filteredParams)
    }

    router.push(`/${linkPrefix}?${queryString}`, { scroll: false })
  }

  function submitSearchOnEnterKeyPress(event) {
    if (event.key === 'Enter') search()
  }

  return (
    <div className="w-full">
      <div className="mx-auto flex w-full flex-col gap-2 font-inter desktop-sm:grid desktop-sm:grid-cols-12 desktop-sm:justify-between desktop-sm:gap-x-8">
        {!hidePlatforms && (
          <div className="col-span-2 hidden desktop-sm:block">
            <div className="text-p-2 text-neutral-blue-200">Platform</div>
          </div>
        )}
        {!hideGame && (
          <div className="col-span-2 hidden desktop-sm:block">
            <div className="text-p-2 text-neutral-blue-200">Game</div>
          </div>
        )}
        {!hideCarClass && (
          <div className="col-span-2 hidden desktop-sm:block">
            <div className="text-p-2 text-neutral-blue-200">Car class</div>
          </div>
        )}
        {!hideDays && (
          <div className="col-span-2 hidden desktop-sm:block">
            <div className="text-p-2 text-neutral-blue-200">Days</div>
          </div>
        )}

        <div className="col-span-4 hidden desktop-sm:block">
          <div className="text-p-2 text-neutral-blue-200">Keyword</div>
        </div>
        <div className="col-span-2 hidden desktop-sm:block" />

        {!hidePlatforms && (
          <div className="w-full desktop-sm:col-span-2">
            <div className="mb-0.5 text-p-2 font-semibold text-neutral-blue-200 desktop-sm:hidden">
              Platform
            </div>
            <SearchElement
              {...searchOptions[0]}
              value={filter.platforms}
              setValue={(newValue) => updateFilter('platforms', newValue)}
              labelKey="nicename"
              valueKey="name"
              isMulti
            />
          </div>
        )}
        {!hideGame && (
          <div className="w-full desktop-sm:col-span-2">
            <div className="mb-0.5 text-p-2 text-neutral-blue-200 desktop-sm:hidden">
              Game
            </div>
            <SearchElement
              {...searchOptions[1]}
              value={filter.game}
              setValue={(newValue) => setGame('game', newValue)}
              labelKey="nicename"
              valueKey="name"
              isMulti={false}
            />
          </div>
        )}
        {!hideCarClass && (
          <div className="w-full desktop-sm:col-span-2">
            <div className="mb-0.5 text-p-2 text-neutral-blue-200 desktop-sm:hidden">
              Car class
            </div>
            <SearchElement
              {...searchOptions[2]}
              value={filter.carClasses}
              setValue={(newValue) => updateFilter('carClasses', newValue)}
              labelKey="nicename"
              valueKey="name"
              isMulti
            />
          </div>
        )}
        {!hideDays && (
          <div className="w-full desktop-sm:col-span-2">
            <div className="mb-0.5 text-p-2 text-neutral-blue-200 desktop-sm:hidden">
              Days
            </div>
            <SearchElement
              {...searchOptions[3]}
              value={filter.days}
              setValue={(newValue) => updateFilter('days', newValue)}
              labelKey="nicename"
              valueKey="name"
              isMulti
            />
          </div>
        )}

        <div className="flex w-full flex-col desktop-sm:col-span-4">
          <div className="mb-0.5 text-p-2 text-neutral-blue-200 desktop-sm:hidden">
            Keyword
          </div>
          <Input
            className="bg-shades-0! px-3! text-p-2! text-neutral-blue-900! placeholder-neutral-blue-500! hover:bg-[#dbdcdd]! focus:bg-shades-0! h-11 w-full rounded-md border border-neutral-blue-500 font-semibold transition-all duration-300 ease-in-out desktop-xs:h-[57px]"
            placeholder={textPlaceholder}
            onKeyUp={submitSearchOnEnterKeyPress}
            value={filter.keyword || ''}
            onChange={(event) =>
              setFilter((prev) => ({ ...prev, keyword: event.target.value }))
            }
            disabled={searching}
          />
        </div>

        <Button
          disabled={searching || !canRunSearch}
          className="bg-gf-gradient text-white! mt-1.5 flex h-[52px] w-full cursor-pointer items-center justify-center gap-1 rounded-md border-0 transition-all duration-300 ease-in-out hover:opacity-85 desktop-xs:h-[57px] desktop-sm:col-span-2 desktop-sm:mt-0"
          onClick={search}
        >
          {searching ? (
            <Loader
              size="sm"
              spinnerFillColor="shades-0"
              spinnerBgColor="primary"
            />
          ) : (
            <SearchIcon className="size-6 min-w-6 stroke-shades-0" />
          )}
          <p className="text-white! text-p-3 transition-all duration-300 ease-in-out">
            Search
          </p>
        </Button>
      </div>
    </div>
  )
}
